import { type FetchResult, type MutationFunctionOptions } from "@apollo/client";
import { PaymentMethod } from "../../../../common/types/enums";
import {
  type FederatedCreateAchPaymentPrefMutation,
  type FederatedCreateAchPaymentPrefMutationVariables,
  type FederatedCreateCheckPaymentPrefMutation,
  type FederatedCreateCheckPaymentPrefMutationVariables,
  type FederatedCreateWirePaymentPrefMutation,
  type FederatedCreateWirePaymentPrefMutationVariables,
  useFederatedCreateAchPaymentPrefMutation,
  useFederatedCreateCheckPaymentPrefMutation,
  useFederatedCreateWirePaymentPrefMutation,
  useUpdatePaymentPrefForPositionsInAccountMutation,
  useUpdatePaymentPrefForPositionsOfPaymentPrefMutation,
  useUpdatePaymentPrefForSinglePositionMutation,
  useUpdatePaymentPrefsForPositionsInAccountWithoutPaymentPrefsMutation,
} from "../../../../graphql/supergraph.index";
import { AssociatePaymentPrefMutationTypeEnum } from "../../Context/enums";

export type CreatePaymentPrefMutationReturnType =
  | {
      createNewPaymentPrefMutation: (
        options?: MutationFunctionOptions<
          FederatedCreateAchPaymentPrefMutation,
          FederatedCreateAchPaymentPrefMutationVariables
        >,
      ) => Promise<FetchResult<FederatedCreateAchPaymentPrefMutation>>;
      resultKey: "createAchPaymentPref";
    }
  | {
      createNewPaymentPrefMutation: (
        options?: MutationFunctionOptions<
          FederatedCreateCheckPaymentPrefMutation,
          FederatedCreateCheckPaymentPrefMutationVariables
        >,
      ) => Promise<FetchResult<FederatedCreateCheckPaymentPrefMutation>>;
      resultKey: "createCheckPaymentPref";
    }
  | {
      createNewPaymentPrefMutation: (
        options?: MutationFunctionOptions<
          FederatedCreateWirePaymentPrefMutation,
          FederatedCreateWirePaymentPrefMutationVariables
        >,
      ) => Promise<FetchResult<FederatedCreateWirePaymentPrefMutation>>;
      resultKey: "createWirePaymentPref";
    };

export const useDetermineCreatePaymentPrefMutation = (
  paymentMethod: PaymentMethod | undefined,
): CreatePaymentPrefMutationReturnType => {
  const [createCheckPaymentPrefMutation] = useFederatedCreateCheckPaymentPrefMutation();
  const [createWirePaymentPrefMutation] = useFederatedCreateWirePaymentPrefMutation();
  const [createAchPaymentPrefMutation] = useFederatedCreateAchPaymentPrefMutation();

  if (paymentMethod === PaymentMethod.ACH) {
    return {
      createNewPaymentPrefMutation: createAchPaymentPrefMutation,
      resultKey: "createAchPaymentPref",
    };
  } else if (paymentMethod === PaymentMethod.Wire) {
    return {
      createNewPaymentPrefMutation: createWirePaymentPrefMutation,
      resultKey: "createWirePaymentPref",
    };
  } else if (paymentMethod === PaymentMethod.Check) {
    return {
      createNewPaymentPrefMutation: createCheckPaymentPrefMutation,
      resultKey: "createCheckPaymentPref",
    };
  }

  throw new Error("Payment method not supported");
};

export const useDetermineAssociatePaymentPrefMutation = (
  associatePaymentPrefTypeValue: AssociatePaymentPrefMutationTypeEnum,
) => {
  const [updatePositionsInPaymentPrefMutation] =
    useUpdatePaymentPrefForPositionsOfPaymentPrefMutation();
  const [updateSinglePositionMutation] = useUpdatePaymentPrefForSinglePositionMutation();
  const [updatePositionsInAccountMutation] =
    useUpdatePaymentPrefForPositionsInAccountMutation();
  const [updatePaymentPrefsForPositionsInAccountWithoutPaymentPrefs] =
    useUpdatePaymentPrefsForPositionsInAccountWithoutPaymentPrefsMutation();

  if (
    associatePaymentPrefTypeValue ===
    AssociatePaymentPrefMutationTypeEnum.AllOtherPositionsWithPaymentPref
  ) {
    return {
      associatePaymentPrefMutation: updatePositionsInPaymentPrefMutation,
      associatePaymentPrefKey: "updatePaymentPrefForPositionsOfPaymentPref",
    };
  } else if (
    associatePaymentPrefTypeValue === AssociatePaymentPrefMutationTypeEnum.OnlyThisPosition
  ) {
    return {
      associatePaymentPrefMutation: updateSinglePositionMutation,
      associatePaymentPrefKey: "updatePaymentPrefForPosition",
    };
  } else if (
    associatePaymentPrefTypeValue ===
    AssociatePaymentPrefMutationTypeEnum.PositionsWithoutPaymentPrefOfAccount
  ) {
    return {
      associatePaymentPrefMutation: updatePaymentPrefsForPositionsInAccountWithoutPaymentPrefs,
      associatePaymentPrefKey: "updatePaymentPrefForPositionsWithoutPaymentPref",
    };
  }

  // TODO Be explicit or handle unsupported workflow, don't default https://junipersquare.atlassian.net/browse/FF-3254
  return {
    associatePaymentPrefMutation: updatePositionsInAccountMutation,
    associatePaymentPrefKey: "updatePaymentPrefForAccounts",
  };
};
